import { getAxiosClient } from '../../client';
import { makeApiRequest } from '../../makeApiRequest';
import type { ApiResponse } from '../../responses';
import { AnalyticsEndpoints } from './analyticsEndpoints';
import { mapFinancialPosition } from './analyticsMapping';
import type {
    FinancialPositionReadOptions,
    IAnalyticsService,
} from './IAnalyticsService';
import type { GetFinancialPositionResponse } from './responses';

type AnalyticsServiceOptions = {
    baseUrl: string;
};

/**
 * A body this client cannot read as a financial position is contract drift, not
 * a figure to guess at. Mirrors `UNMAPPABLE_STATEMENT`: the cards' own
 * error-with-retry is the correct outcome, and far safer than rendering half a
 * position as though it were the whole one.
 */
const UNMAPPABLE_POSITION: ApiResponse<never> = {
    success: false,
    error: {
        isAxiosError: false,
        status: 500,
        name: '500',
        message:
            'The server returned a financial position this client cannot read.',
        code: 'UNMAPPABLE_FINANCIAL_POSITION',
    },
};

/** The live ledger-analytics client (bos-api). */
export class AnalyticsService implements IAnalyticsService {
    private readonly baseUrl: string;

    public constructor({ baseUrl }: AnalyticsServiceOptions) {
        this.baseUrl = baseUrl;
    }

    public async getFinancialPosition({
        asOf,
        locale,
    }: FinancialPositionReadOptions): Promise<
        ApiResponse<GetFinancialPositionResponse>
    > {
        const result = await makeApiRequest<unknown>(
            getAxiosClient({ baseURL: this.baseUrl }).get(
                AnalyticsEndpoints.financialPosition({ asOf, locale }),
            ),
        );

        if (!result.success) {
            return result;
        }

        const mapped = mapFinancialPosition(result.response);
        return mapped
            ? { success: true, response: mapped }
            : UNMAPPABLE_POSITION;
    }
}
